<template>
  <div class="container">
    <a-button
      type="primary"
      @click="openAdd = true"
      style="margin-bottom: 16px"
    >
      添加日记
    </a-button>
    <a-table
      :columns="columns"
      :data-source="journalList"
      :pagination="{
        current: listRequest.current,
        pageSize: listRequest.size,
        total: total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 30, 40, 50],
        onChange: handleChange,
        onShowSizeChange: handleSizeChange,
      }"
      bordered
    >
      <template
        #bodyCell="{ column, record }: { column: ColumnType, record: Journal }"
      >
        <template v-if="column.dataIndex === 'is_delete'">
          <a-tag v-if="record.is_delete === 1" color="green">正常</a-tag>
          <a-tag v-if="record.is_delete === 2" color="red">回收站</a-tag>
        </template>
        <template v-else-if="column.dataIndex === 'create_at'">
          <span
            >{{ dayjs(record.create_at).format("YYYY-MM-DD HH:mm:ss") }}
          </span>
        </template>
        <template v-else-if="column.dataIndex === 'operator'">
          <a-button
            @click="handleUpdate(record)"
            type="primary"
            :style="{ marginRight: '8px' }"
            >编辑
          </a-button>
          <a-button
            @click="submitDelete(record)"
            type="primary"
            :style="{
              backgroundColor: '#ff7a45',
              color: 'white',
              marginRight: '8px',
            }"
            >删除
          </a-button>
          <a-button
            @click="submitDrop(record)"
            type="primary"
            :style="{
              backgroundColor: '#ff4d4f',
              color: 'white',
            }"
            >彻底删除
          </a-button>
        </template>
      </template>
    </a-table>
    <a-modal
      v-model:open="openAdd"
      title="添加日记数据"
      @ok="submitAdd"
      cancelText="取消"
    >
      <a-form :model="addRequest">
        <a-form-item label="会员">
          <a-select v-model:value="addRequest.user_id">
            <a-select-option
              v-for="user in userList"
              :value="user.id"
              :key="user.id"
              >{{ user.id }} - {{ user.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="标题">
          <a-input v-model:value="addRequest.title" />
        </a-form-item>
        <a-form-item label="内容">
          <a-textarea v-model:value="addRequest.content" :rows="4" />
        </a-form-item>
      </a-form>
    </a-modal>
    <a-modal
      v-model:open="openUpdate"
      title="修改日记数据"
      @ok="submitUpdate"
      cancelText="取消"
    >
      <a-form :model="updateRequest" style="margin-top: 20px">
        <a-form-item label="标题">
          <a-input v-model:value="updateRequest.title" />
        </a-form-item>
        <a-form-item label="内容">
          <a-textarea v-model:value="updateRequest.content" :rows="4" />
        </a-form-item>
        <a-form-item label="状态">
          <a-select v-model:value="updateRequest.is_delete">
            <a-select-option :value="1">正常</a-select-option>
            <a-select-option :value="2">回收站</a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { JournalService } from "@/api/services/JournalService";
import { message } from "ant-design-vue";
import dayjs from "dayjs";
import { ColumnType } from "ant-design-vue/lib/table";
import { Journal } from "@/api/models/journal/Journal";
import { JournalAddRequest } from "@/api/models/journal/JournalAddRequest";
import { JournalListRequest } from "@/api/models/journal/JournalListRequest";
import { JournalUpdateRequest } from "@/api/models/journal/JournalUpdateRequest";
import { User } from "@/api/models/user/User";
import { UserService } from "@/api/services/UserService";

const total = ref<number>(0);

const listRequest = reactive<JournalListRequest>({
  current: 1,
  size: 20,
  content: "",
  user_id: 0,
});

const journalList = ref<Journal[]>([]);

const handleList = async () => {
  const res = await JournalService.ListAll(listRequest);
  if (res.code === 0) {
    journalList.value = res.data.records;
    total.value = res.data.total;
  } else {
    message.error(res.msg);
  }
};

const userList = ref<User[]>([]);

const handleUserList = async () => {
  const res = await UserService.ListAll({ current: 0, size: 0 });
  if (res.code === 0) {
    userList.value = res.data.records;
  } else {
    message.error(res.msg);
  }
};

onMounted(async () => {
  await handleList();
  await handleUserList();
});

const handleSizeChange = (current: number, pageSize: number) => {
  listRequest.current = current;
  listRequest.size = pageSize;
  handleList();
};

const handleChange = (page: number, pageSize: number) => {
  listRequest.current = page;
  listRequest.size = pageSize;
  handleList();
};

const openAdd = ref(false);

const addRequest = reactive<JournalAddRequest>({
  content: "",
  title: "",
});

const submitAdd = async () => {
  const res = await JournalService.Add(addRequest);
  if (res.code === 0) {
    message.success("添加成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const openUpdate = ref(false);

const updateRequest = reactive<JournalUpdateRequest>({
  id: 0,
  content: "",
  is_delete: 1,
  title: "",
});

const handleUpdate = (record: Journal) => {
  openUpdate.value = true;
  Object.assign(updateRequest, record);
};

const submitUpdate = async () => {
  const res = await JournalService.Update(updateRequest);
  if (res.code === 0) {
    message.success("修改成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const submitDelete = async (record: Journal) => {
  const res = await JournalService.Delete(record.id);
  if (res.code === 0) {
    message.success("删除成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const submitDrop = async (record: Journal) => {
  const res = await JournalService.Drop(record.id);
  if (res.code === 0) {
    message.success("删除成功");
    setTimeout(() => location.reload(), 1000);
  } else {
    message.error(res.msg);
  }
};

const columns: ColumnType[] = [
  {
    title: "Id",
    dataIndex: "id",
    align: "center",
  },
  {
    title: "用户id",
    dataIndex: "user_id",
    align: "center",
  },
  {
    title: "内容",
    dataIndex: "content",
    align: "center",
  },
  {
    title: "状态",
    dataIndex: "is_delete",
    align: "center",
  },
  {
    title: "创建时间",
    dataIndex: "create_at",
    align: "center",
  },
  {
    title: "操作",
    dataIndex: "operator",
    align: "center",
  },
];
</script>

<style scoped>
.container {
  border: 1px solid #d9d9d9;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 98%;
  margin-left: 1%;
  background-color: white;
  margin-top: 16px;
}
</style>
